import * as React from 'react';
import { RadioGroupItem, Label, cn } from '@bliks-io/ui';

/**
 * RadioCard - A card-style radio button option with embedded content support
 *
 * Compositional components for creating clickable radio cards with icons, labels, and content.
 * The entire card is clickable to select the radio option.
 *
 * @example
 * ```tsx
 * <RadioGroup value={selected} onValueChange={setSelected}>
 *   <RadioCard value="option1" isActive={selected === 'option1'}>
 *     <RadioCardHeader>
 *       <RadioCardItem value="option1" />
 *       <RadioCardIcon icon={UserIcon} />
 *       <RadioCardLabel>Option 1</RadioCardLabel>
 *       <RadioCardDescription>Description text</RadioCardDescription>
 *     </RadioCardHeader>
 *     <RadioCardContent>
 *       <Input placeholder="Additional input" />
 *     </RadioCardContent>
 *   </RadioCard>
 * </RadioGroup>
 * ```
 */

interface RadioCardProps extends React.ComponentPropsWithoutRef<typeof Label> {
  value: string;
  isActive: boolean;
  children: React.ReactNode;
}

const RadioCard = React.forwardRef<HTMLLabelElement, RadioCardProps>(
  ({ value, isActive, className, children, ...props }, ref) => {
    return (
      <Label
        ref={ref}
        htmlFor={value}
        className={cn(
          'flex flex-col gap-3 rounded-lg border-2 p-4 transition-colors cursor-pointer',
          isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          className
        )}
        {...props}
      >
        {children}
      </Label>
    );
  }
);
RadioCard.displayName = 'RadioCard';

/**
 * RadioCardHeader - Container for the radio button, icon, label, and description
 */
interface RadioCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const RadioCardHeader = React.forwardRef<HTMLDivElement, RadioCardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-start gap-3', className)} {...props}>
        {children}
      </div>
    );
  }
);
RadioCardHeader.displayName = 'RadioCardHeader';

/**
 * RadioCardItem - The radio button itself
 */
interface RadioCardItemProps extends React.ComponentPropsWithoutRef<typeof RadioGroupItem> {
  value: string;
}

const RadioCardItem = React.forwardRef<React.ElementRef<typeof RadioGroupItem>, RadioCardItemProps>(
  ({ value, className, ...props }, ref) => {
    return (
      <RadioGroupItem
        ref={ref}
        value={value}
        id={value}
        className={cn('mt-0.5', className)}
        {...props}
      />
    );
  }
);
RadioCardItem.displayName = 'RadioCardItem';

/**
 * RadioCardIcon - Icon displayed in the header
 */
interface RadioCardIconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: React.ComponentType<{ className?: string }>;
}

const RadioCardIcon = React.forwardRef<HTMLDivElement, RadioCardIconProps>(
  ({ icon: Icon, className, ...props }, ref) => {
    return (
      <div ref={ref} {...props}>
        <Icon className={cn('h-5 w-5 text-muted-foreground mt-0.5 shrink-0', className)} />
      </div>
    );
  }
);
RadioCardIcon.displayName = 'RadioCardIcon';

/**
 * RadioCardLabel - Primary label text
 */
interface RadioCardLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const RadioCardLabel = React.forwardRef<HTMLDivElement, RadioCardLabelProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('font-medium text-sm', className)} {...props}>
        {children}
      </div>
    );
  }
);
RadioCardLabel.displayName = 'RadioCardLabel';

/**
 * RadioCardDescription - Secondary description text
 */
interface RadioCardDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const RadioCardDescription = React.forwardRef<HTMLDivElement, RadioCardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('text-xs text-muted-foreground mt-0.5', className)} {...props}>
        {children}
      </div>
    );
  }
);
RadioCardDescription.displayName = 'RadioCardDescription';

/**
 * RadioCardText - Container for label and description
 */
interface RadioCardTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const RadioCardText = React.forwardRef<HTMLDivElement, RadioCardTextProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex-1', className)} {...props}>
        {children}
      </div>
    );
  }
);
RadioCardText.displayName = 'RadioCardText';

/**
 * RadioCardContent - Content area for embedded inputs/content
 * Automatically prevents clicks from propagating to the card label
 */
interface RadioCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const RadioCardContent = React.forwardRef<HTMLDivElement, RadioCardContentProps>(
  ({ className, onClick, children, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick?.(e);
    };

    return (
      <div ref={ref} className={cn('ml-10 mt-1', className)} onClick={handleClick} {...props}>
        {children}
      </div>
    );
  }
);
RadioCardContent.displayName = 'RadioCardContent';

export {
  RadioCard,
  RadioCardHeader,
  RadioCardItem,
  RadioCardIcon,
  RadioCardLabel,
  RadioCardDescription,
  RadioCardText,
  RadioCardContent
};
